// Feels Sharing API client — the ONLY way our example apps (Feels Drawer,
// Scream Room) obtain/share Feels. This speaks the externalized
// `integration-partner-api` **/v1** contract (GET /search, GET /collections,
// GET /collections/:id, POST /shares) that 3rd parties (Instagram, Facebook,
// Snapchat…) use to put a Feels picker in their own chat drawers.
//
// Auth mirrors the decided partner model (docs/CONTRACT.md): the shared demo
// key is injected SERVER-side, never shipped to the browser. Our "partner
// backend" is the admin API's /1.0/feels-sharing proxy (admin-session cookie,
// same as the Scream Room chat) which forwards verbatim to /v1 — so these
// pages still program against the real /v1 request/response shapes.
//
// There is intentionally NO local/fixture fallback: these are reference builds
// for external partners, so a failing API must surface as a real failure (not
// be masked by bundled sample data). If a call fails, it throws — the pages
// show the error so we know the integration is broken.
//
// The /v1 `Feel` object (what every function here resolves with):
//   { id, song: { name }, artist: { name }, lyric: { text },
//     media: { poster, video, story, aspect, duration_ms } }

const BASE = (process.env.REACT_APP_BACKEND_BASE_URL || '').replace(/\/+$/, '')
const ENDPOINT = `${BASE}/1.0/feels-sharing`

// Mood buckets are an example-app UX; against the real API they map to searches.
const BUCKET_QUERY = {
  'I love you': 'love',
  'I am sad': 'sad',
  Heartbreak: 'without you',
  Hype: 'money',
  Party: 'dance',
}

export const BUCKETS = Object.keys(BUCKET_QUERY)

async function api(path, { method = 'GET', body } = {}) {
  let res
  try {
    res = await fetch(`${ENDPOINT}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (e) {
    // Network/CORS-level failure (no HTTP status reached the browser).
    throw new Error(`Feels Sharing API unreachable: ${e.message}`)
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    // /v1 typed error envelope: { error: { code, message, retryable } }
    const code = (data && data.error && data.error.code) || res.status
    throw new Error(`Feels Sharing API error: ${path.split('?')[0]} returned ${code}`)
  }
  return data
}

// Phrase search (matches lyric AND title). Returns /v1 Feels. Throws on failure.
async function apiSearch(query) {
  const data = await api(`/search?q=${encodeURIComponent(query)}`)
  return Array.isArray(data.feelses) ? data.feelses : []
}

// Feels for a mood bucket (mapped to a /v1 search). Throws on failure.
export async function feelsForBucket(bucket) {
  return apiSearch(BUCKET_QUERY[bucket] || bucket)
}

// Free-text search for the Feels picker. Throws on failure.
// NOTE: /v1/search requires a query — for a no-query "browse" list use
// trendingFeels() (the default Search landing collection), not searchFeels('').
export async function searchFeels(query) {
  const q = (query || '').trim()
  if (!q) return trendingFeels()
  return apiSearch(q)
}

// A default "browse" set of Feels with no search query: list the Search landing
// collections (GET /collections) and load the default one's feels
// (GET /collections/:id). Throws on failure.
export async function trendingFeels() {
  const listing = await api('/collections')
  const collections = Array.isArray(listing.collections) ? listing.collections : []
  if (collections.length === 0) return []
  const chosen = collections.find((c) => c.default) || collections[0]
  const page = await api(`/collections/${encodeURIComponent(chosen.id)}`)
  return Array.isArray(page.feelses) ? page.feelses : []
}

// Commit a share for a picked Feel (POST /shares — the only /v1 write).
// Resolves with { feels, share: { url, link_out, og } }; the example chat can
// insert share.url. Throws on failure.
export async function sendFeel(feelsId) {
  return api('/shares', { method: 'POST', body: { feels_id: feelsId } })
}
